import { ChevronDown } from "lucide-react";
import type { LibraryCompleteness, LibraryCounts } from "#shared/schemas/appStatus.ts";
import { useIsAdmin } from "@web/features/auth/useMe.ts";
import { useAppStatus } from "@web/features/library/api.ts";
import { usePlayerStore } from "@web/features/player/playerStore.ts";
import { Button } from "@web/components/ui/button.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import { cn } from "@web/lib/utils.ts";

type DotTone = "ok" | "warn" | "bad" | "idle";

function StatusDot({ tone }: { tone: DotTone }) {
  return (
    <span
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        tone === "ok" && "bg-accent",
        tone === "warn" && "bg-status-working",
        tone === "bad" && "bg-status-cancelled",
        tone === "idle" && "bg-faint",
      )}
      aria-hidden
    />
  );
}

function Row({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  tone: DotTone;
}) {
  return (
    <div className="flex items-start gap-3 border-b border-line py-3 last:border-b-0">
      <div className="mt-1.5">
        <StatusDot tone={tone} />
      </div>
      <div className="w-24 shrink-0 text-sm text-muted">{label}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text">{value}</p>
        {detail && <p className="mt-0.5 text-xs text-faint">{detail}</p>}
      </div>
    </div>
  );
}

function formatAgo(iso: string | null | undefined, fallback: string): string {
  if (!iso) return fallback;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return fallback;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const days = Math.round(hr / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function fmt(n: number): string {
  return n.toLocaleString();
}

function CompletenessLine({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className="text-sm text-text">{label}</p>
        {hint ? <p className="text-xs text-faint">{hint}</p> : null}
      </div>
      <p className="shrink-0 font-mono text-sm tabular-nums text-text">{value}</p>
    </div>
  );
}

function CompletenessSummary({ c }: { c: LibraryCompleteness }) {
  const ownedFilled =
    c.playlists_owned === 0
      ? null
      : Math.min(c.playlists_with_tracks, c.playlists_owned);
  const ownedGap =
    ownedFilled == null ? 0 : Math.max(0, c.playlists_owned - ownedFilled);

  let headline = "Nothing synced yet — run Sync library";
  let tone: DotTone = "idle";
  if (c.liked > 0 || c.playlists_listed > 0) {
    if (ownedGap === 0 && c.playlists_empty <= c.playlists_followed) {
      headline = "Looks complete for your account";
      tone = "ok";
    } else if (ownedGap > 0) {
      headline = `${fmt(ownedGap)} owned playlist${ownedGap === 1 ? "" : "s"} still empty`;
      tone = "warn";
    } else {
      headline = "Partially synced";
      tone = "warn";
    }
  }

  return (
    <details open className="group border-t border-line pt-3">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-text [&::-webkit-details-marker]:hidden">
        <StatusDot tone={tone} />
        <span className="flex-1">Your Spotify data</span>
        <span className="hidden text-xs font-normal text-faint sm:inline">{headline}</span>
        <ChevronDown
          className="size-3.5 shrink-0 text-muted transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>

      <div className="mt-3 space-y-1 rounded-md border border-line bg-bg/40 px-3 py-2">
        <p className="pb-1 text-xs text-faint">
          Only <span className="text-muted">your</span> connected Spotify account — not the whole
          CoyKoi database. Match these to Liked Songs / Your Library in Spotify.
        </p>
        <p className="pb-1 text-xs font-medium text-muted sm:hidden">{headline}</p>

        <CompletenessLine
          label="Liked songs"
          value={fmt(c.liked)}
          hint="Spotify → Liked Songs"
        />
        <CompletenessLine
          label="Playlists"
          value={fmt(c.playlists_listed)}
          hint={`${fmt(c.playlists_owned)} owned · ${fmt(c.playlists_followed)} followed`}
        />
        <CompletenessLine
          label="Playlists with songs pulled"
          value={`${fmt(c.playlists_with_tracks)} / ${fmt(c.playlists_listed)}`}
          hint={
            c.playlists_empty > 0
              ? `${fmt(c.playlists_empty)} still empty here`
              : "Every listed playlist has tracks in CoyKoi"
          }
        />
        {c.playlists_items_inaccessible > 0 ? (
          <CompletenessLine
            label="Playlists Spotify blocked"
            value={fmt(c.playlists_items_inaccessible)}
            hint="Usually followed playlists (403 on /items). Sync again after a CoyKoi update — we no longer skip them up front"
          />
        ) : null}
        <CompletenessLine
          label="Songs in your playlists"
          value={fmt(c.playlist_track_rows)}
          hint="Counted per playlist (duplicates across playlists add up)"
        />
        <CompletenessLine
          label="Unique songs"
          value={fmt(c.unique_tracks)}
          hint="Liked + playlist songs, counted once"
        />
        <CompletenessLine label="Albums" value={fmt(c.unique_albums)} />
        <CompletenessLine label="Artists" value={fmt(c.unique_artists)} />
      </div>
    </details>
  );
}

/** Admin-only: shared SQLite catalog size (everyone’s mirrored rows). */
function SharedCatalogSummary({ counts }: { counts: LibraryCounts }) {
  return (
    <details className="group border-t border-line pt-3">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-muted [&::-webkit-details-marker]:hidden">
        <StatusDot tone="idle" />
        <span className="flex-1">Shared catalog (admin)</span>
        <span className="text-xs font-normal text-faint">
          {fmt(counts.tracks)} tracks in SQLite
        </span>
        <ChevronDown
          className="size-3.5 shrink-0 text-muted transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="mt-3 space-y-1 rounded-md border border-line bg-bg/40 px-3 py-2">
        <p className="pb-1 text-xs text-faint">
          Whole DB mirror — all users’ Spotify pulls land here. Regular users don’t need this.
        </p>
        <CompletenessLine label="Tracks" value={fmt(counts.tracks)} />
        <CompletenessLine label="Liked rows (all users)" value={fmt(counts.liked)} />
        <CompletenessLine label="Albums" value={fmt(counts.albums)} />
        <CompletenessLine label="Artists" value={fmt(counts.artists)} />
        <CompletenessLine label="Playlists" value={fmt(counts.playlists)} />
      </div>
    </details>
  );
}

/** User-visible health — answers “is it me or is it broken?” */
export function ConnectionStatusPanel() {
  const isAdmin = useIsAdmin();
  const status = useAppStatus({ refetchInterval: 10_000 });
  const playerStatus = usePlayerStore((s) => s.status);
  const playerReady = usePlayerStore((s) => s.ready);
  const playerConnected = usePlayerStore((s) => s.connected);

  const checkedLabel = !status.dataUpdatedAt
    ? "not checked yet"
    : `last checked ${Math.max(0, Math.round((Date.now() - status.dataUpdatedAt) / 1000))}s ago`;

  if (status.isLoading && !status.data) {
    return (
      <section className="rounded-lg border border-line bg-panel px-4 py-4">
        <h2 className="text-sm font-semibold">Connection status</h2>
        <p className="mt-3 text-sm text-muted">Checking…</p>
      </section>
    );
  }

  if (status.isError && !status.data) {
    return (
      <section className="rounded-lg border border-line bg-panel px-4 py-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Connection status</h2>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void status.refetch()}
          >
            Refresh
          </Button>
        </div>
        <QueryErrorState
          error={status.error}
          onRetry={() => void status.refetch()}
          actionLabel="status panel"
          className="min-h-40 py-6"
        />
      </section>
    );
  }

  const s = status.data;
  if (!s) {
    return (
      <section className="rounded-lg border border-line bg-panel px-4 py-4">
        <EmptyState title="No status" description="Could not load connection status." />
      </section>
    );
  }

  const serverTone: DotTone = status.isFetching && status.isError ? "bad" : "ok";
  const spotifyTone: DotTone = !s.connected
    ? "warn"
    : !s.api_ok
      ? "bad"
      : "ok";
  const spotifyValue = !s.connected
    ? "not connected"
    : !s.api_ok
      ? "error"
      : "connected";
  const spotifyDetail = !s.connected
    ? "Connect Spotify from the sidebar"
    : !s.api_ok
      ? (s.error ?? "Spotify API not responding")
      : "token valid, refreshes automatically";

  let playbackTone: DotTone = "idle";
  let playbackValue = "idle";
  let playbackDetail = "Player not started yet";
  if (playerStatus.isError) {
    const msg = playerStatus.message;
    if (/premium/i.test(msg)) {
      playbackTone = "warn";
      playbackValue = "unavailable";
      playbackDetail = "Premium required";
    } else {
      playbackTone = "bad";
      playbackValue = "unavailable";
      playbackDetail = msg || "blocked on this network";
    }
  } else if (playerReady && playerConnected) {
    playbackTone = "ok";
    playbackValue = "ready";
    playbackDetail = "Web Playback SDK connected";
  } else if (playerStatus.message) {
    playbackTone = "idle";
    playbackValue = "connecting";
    playbackDetail = playerStatus.message;
  }

  const lastSyncAt = s.sync?.finished_at ?? s.sync?.started_at ?? null;
  const syncTone: DotTone = s.sync?.status === "error" ? "bad" : lastSyncAt ? "ok" : "idle";
  const syncValue = lastSyncAt ? formatAgo(lastSyncAt, "unknown") : "never";
  const syncDetail =
    s.sync?.status === "error"
      ? (s.sync.error ?? "last sync failed")
      : s.sync
        ? `${s.sync.status} · ${s.sync.api_calls} API calls`
        : "Run Sync from the sidebar";

  const c = s.completeness;

  return (
    <section className="rounded-lg border border-line bg-panel px-4 py-4">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Connection status</h2>
          <p className="mt-0.5 text-xs text-faint">Is it you, or is it broken?</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={status.isFetching}
          onClick={() => void status.refetch()}
        >
          Refresh
        </Button>
      </div>

      <div className="mt-2">
        <Row label="Server" value="ok" detail={checkedLabel} tone={serverTone} />
        <Row label="Spotify" value={spotifyValue} detail={spotifyDetail} tone={spotifyTone} />
        <Row
          label="Playback"
          value={playbackValue}
          detail={playbackDetail}
          tone={playbackTone}
        />
        <Row label="Last sync" value={syncValue} detail={syncDetail} tone={syncTone} />
      </div>

      <CompletenessSummary c={c} />
      {isAdmin ? <SharedCatalogSummary counts={s.counts} /> : null}
    </section>
  );
}
